/**
 * cosmeticIcon — compose a candidate {@link CharacterIcon} by laying a shop item
 * ON TOP of the player's CURRENT icon, so the storefront can show a LIVE 3D
 * preview of "your character WITH this cosmetic equipped" before purchase.
 *
 * Returns null when the item doesn't change the character FIGURE (seat-tile
 * cosmetics, titles, particles, emotes) — those have no avatar representation,
 * so the shop keeps its existing 2D chip/seat preview for them.
 *
 * The kind → icon-field mapping mirrors `CosmeticPreview` + the
 * `@tt/lobby/cosmetic-resolver` storage convention, so the preview reflects
 * exactly what equipping the item will do.
 */
import type { CharacterIcon } from '@tt/types';
import { getCosmetic } from '@tt/lobby/cosmetics-catalog';
import { resolveCosmeticToInventory } from '@tt/lobby/cosmetic-resolver';
import type { ShopItem } from '@tt/lobby/shop';

/** Lantern-shop catalog id → candidate icon (or null if not a figure cosmetic). */
export function composeIconWithCosmetic(
  base: CharacterIcon,
  cosmeticId: string,
): CharacterIcon | null {
  if (!getCosmetic(cosmeticId)) return null;
  const resolved = resolveCosmeticToInventory(cosmeticId);
  if (!resolved) return null;
  const next: CharacterIcon = { ...base };
  switch (resolved.kind) {
    case 'body':       next.body = resolved.value as number; return next;
    case 'hat':        next.hat = resolved.value as number; return next;
    case 'face':       next.face = resolved.value as number; return next;
    case 'accent':     next.accentHue = resolved.value as number; return next;
    case 'palette':    next.paletteId = resolved.value as string; return next;
    case 'border':     next.border = resolved.value as string; return next;
    case 'background': next.background = resolved.value as string; return next;
    case 'mask':       next.mask = resolved.value as string; return next;
    case 'outfit':     next.outfit = resolved.value as string; return next;
    // Ambient particles (avatar `particles` kind) → 3D particle system.
    case 'particles':  next.particles = resolved.value as string; return next;
    // ── Repurposed SEAT-tile family → 3D CHARACTER effects (the 2D seat tile
    //    is retired; these keep value as showcase effects on the figure). The
    //    shell's builders alias the seat ids onto the matching avatar theme. ──
    case 'seat-particle':   next.particles = resolved.value as string; return next;
    case 'seat-background': next.background = resolved.value as string; return next;
    case 'seat-border':     next.border = resolved.value as string; return next;
    case 'seat-bghue':      next.rimHue = resolved.value as number; return next;
    // title — no character-figure representation.
    default: return null;
  }
}

/** Embers-shop {@link ShopItem} → candidate icon (or null if not a figure
 *  cosmetic). The embers catalog keys on inventory bucket + ref, not catalog id. */
export function composeIconWithShopItem(
  base: CharacterIcon,
  item: ShopItem,
): CharacterIcon | null {
  const next: CharacterIcon = { ...base };
  switch (item.bucket) {
    case 'hues':       next.hue = Number(item.ref); return next;
    case 'accentHues': next.accentHue = Number(item.ref); return next;
    case 'bodies':     next.body = Number(item.ref); return next;
    case 'hats':       next.hat = Number(item.ref); return next;
    case 'faces':      next.face = Number(item.ref); return next;
    case 'palettes':   next.paletteId = String(item.ref); return next;
    // titles / emotes / seat* buckets don't change the figure.
    default: return null;
  }
}
